// Single source of truth for "where does this persona live".
//
// This previously existed as THREE separate maps that had already drifted apart:
//   - src/pages/Login/LoginPage.jsx   (post-login redirect)
//   - src/components/NavBar.jsx       (logo click)
//   - src/utils/menu.js               (sidebar Dashboard item)
// They disagreed on Interior Designer, BV Associate and Zumale User, and two of them
// pointed BV Associates at `/bv-user-dashboard`, which has NO route defined — so
// clicking the logo or the sidebar Dashboard as a BV Associate hit <Unauthorized/>.

import { APP_IDS } from "./appIdentity";

// Every route below is verified to exist in src/App.js.
export const PERSONA_LANDING = {
  DEVELOPER_ADMIN: "/admin-dashboard",
  STAFF: "/admin-dashboard",
  // Two dashboards exist for this persona; `/zumale_user-dashboard` is the one the
  // login flow has always used, so it stays canonical.
  ZUMALE_USER: "/zumale_user-dashboard",
  INTERIOR_DESIGNER: "/interior-designer/leads",
  // NOT `/bv-user-dashboard` — that route does not exist.
  BV_ASSOCIATE: "/bv-user/buyers",
  ARCHITECT: "/architect/dashboard",
  FREELANCER: "/freelancer/dashboard",
  CHANNEL_PARTNER: "/agent-dashboard",
  PROPERTY_BUYER: "/user-dashboard",
  // v4_6 — an execution vendor lands on their sites, because that is the only thing they do here.
  VENDOR: "/vendor/sites",
};

export const landingFor = (persona) => PERSONA_LANDING[persona] || "/admin-dashboard";

// Where the sidebar's "Dashboard" item points. Usually the same place a persona lands after
// login, but not always: a Zumale admin lands on the customer list (that is the working surface
// they want first), while `/zumale-admin-dashboard` is the actual dashboard. Pointing the
// Dashboard item at the landing route made it a duplicate of "Customers" and left the real
// dashboard unreachable from the sidebar.
export const PERSONA_DASHBOARD = {
  ZUMALE_USER: "/zumale-admin-dashboard",
};

export const dashboardFor = (persona) => PERSONA_DASHBOARD[persona] || landingFor(persona);

// Which surface serves each persona. Mirrors APP_PERSONAS on the backend
// (services/shared/shared_layer/schemas/customer.py) — keep the two in step.
export const PERSONA_APP = {
  DEVELOPER_ADMIN: APP_IDS.WEB_APP,
  STAFF: APP_IDS.WEB_APP,
  ZUMALE_USER: APP_IDS.WEB_APP,
  INTERIOR_DESIGNER: APP_IDS.WEB_INTERIOR,
  BV_ASSOCIATE: APP_IDS.WEB_INTERIOR,
  ARCHITECT: APP_IDS.WEB_ARCH,
  FREELANCER: APP_IDS.WEB_ARCH,
  // Mobile-only: these have no web surface at all.
  CHANNEL_PARTNER: APP_IDS.MOBILE_PARTNER,
  PROPERTY_BUYER: APP_IDS.MOBILE_BUYER,
};


// Which personas can SIGN UP, and on which surface. The login page filters its signup
// drawer through this, so interior.buildvizer.com offers only Interior Designer and
// arch.buildvizer.com only Architect/Freelancer.
//
// The backend enforces the same rule (assert_app_serves_persona on each signup
// endpoint) — this list is the UX half. Offering a signup the backend will refuse
// creates an account in a dead end: it succeeds, then the person cannot log in where
// they created it.
//
// Buyers and channel partners are absent because neither self-signs-up anywhere:
// buyers are created by the sale flow, CPs by a developer or BuildVizer.
export const SIGNUP_OPTIONS = [
  {
    slug: "interior-designer",
    persona: "INTERIOR_DESIGNER",
    title: "Interior Designer",
    description: "Create designs and manage projects",
  },
  {
    slug: "architect",
    persona: "ARCHITECT",
    title: "Architect",
    description: "Delegate drafting & detailing to trusted talent",
  },
  {
    slug: "freelancer",
    persona: "FREELANCER",
    title: "Freelancer",
    description: "Grow your architecture career with paid projects",
  },
];

/**
 * Signup options this surface can actually complete.
 *
 * With no app id — local development, or a build whose env.js failed to generate —
 * every option is returned, matching the backend, which applies no filtering when it
 * cannot identify the caller.
 */
export const signupOptionsForApp = (appId) =>
  !appId ? SIGNUP_OPTIONS : SIGNUP_OPTIONS.filter((o) => PERSONA_APP[o.persona] === appId);
